import {
  canViewDataExplorerVerifEye,
  canViewVerifeyePage,
  useUserHasFeature,
  useUserSession,
} from '@solinkcorp/authentication';
import { LocaleProvider } from '@solinkcorp/locale';
import {
  DeviceLocalityProviderWrapper,
  LiveVideoProvider,
  PeopleTrackingProvider,
  SDMAnalyticsProvider,
  SDMClientProvider,
  type SDMClient,
  type ServiceDisplayLayout,
} from '@solinkcorp/service-display-monitor';
import { CallhomeApi, VmsApi } from 'clients';
import usePeopleTrackingCamera from 'components/CameraList/usePeopleTrackingCamera';
import Loadable from 'components/Loadable';
import { usePlayingCamerasContext } from 'components/PlayingCamerasContext';
import { useDeviceIsLocal } from 'hooks/useDeviceIsLocal';
import AnalyticsLogger from 'models/AnalyticsLogger';
import React, { useMemo } from 'react';
import { Route, Routes } from 'react-router-dom';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import { LocationStore } from 'stores/LocationStore';
import { useDispatch } from 'stores/store';
import { userHasVerifEyeGroups } from 'utils/AlarmUtil';
import FeaturesUtil from 'utils/FeaturesUtil';
import { DATA_EXPLORER_VERIFEYE_URL, VERIFICATION_ROUTE } from 'vd-constants';
import NavigationFrame from './NavigationFrame/NavigationFrame';
import { generateMenu } from './NavigationFrame/menu';
import SdmLiveFeedPlayer from './ServiceDisplayMonitor/SdmLiveFeedPlayer';
import VerifEyeLandingPage from './VerificationCenter/VerifEyeLandingPage';
import VerifyAccess from './VerifyAccess';

const ServiceDisplayMonitorRoutesInner = Loadable({
  loader: () => import('@solinkcorp/service-display-monitor'),
});

const deviceLocalityProvider = { useDeviceIsLocal };

function ServiceDisplayMonitorRoutes() {
  const locale = useLocale();
  const { accessToken } = useUserSession();

  const sdmClient = useMemo((): SDMClient => {
    const ch = CallhomeApi(accessToken);
    return {
      getServiceDisplayLayouts: () => ch.getServiceDisplayLayouts(),
      getCustomerDefaultSdmLayout: (customerId: string) =>
        ch.getCustomerDefaultSdmLayout(customerId),
      createServiceDisplayLayout: <T extends ServiceDisplayLayout = ServiceDisplayLayout>(
        data: Pick<T, 'customerId' | 'name'>,
      ) =>
        ch.createServiceDisplayLayout(
          data as Parameters<typeof ch.createServiceDisplayLayout>[0],
        ) as unknown as Promise<T>,
      updateServiceDisplayLayout: (id: string, data: { name?: string }) =>
        ch.updateServiceDisplayLayout(id, data) as unknown as Promise<ServiceDisplayLayout>,
      deleteServiceDisplayLayout: (id: string) => ch.deleteServiceDisplayLayout(id),
      updateServiceDisplayWidgets: (layoutId: string, data: any) =>
        ch.updateServiceDisplayWidgets(layoutId, data),
      deleteServiceDisplayWidgets: (layoutId: string, widgetIds: string[]) =>
        ch.deleteServiceDisplayWidgets(layoutId, widgetIds),
      bulkDeleteServiceDisplayDayPartThresholds: (thresholdIds: string[]) =>
        ch.bulkDeleteServiceDisplayDayPartThresholds(thresholdIds),
      updateCustomerDefaultSdmLayout: (customerId: string, id: string | null) =>
        ch.updateCustomerDefaultSdmLayout(customerId, id),
      getServiceDisplayDayParts: (options?: { customerId?: string }) =>
        ch.getServiceDisplayDayParts(options),
      createServiceDisplayDayPart: (data: {
        customerId: string;
        label: string;
        dayParts?: Record<string, { start: string; end: string }[]>;
      }) =>
        ch.createServiceDisplayDayPart({
          ...data,
          dayParts: data.dayParts ?? {},
        } as Parameters<typeof ch.createServiceDisplayDayPart>[0]),
      updateServiceDisplayDayPart: (
        id: string,
        data: Partial<{
          label: string;
          dayParts: Record<string, { start: string; end: string }[]>;
        }>,
      ) => ch.updateServiceDisplayDayPart(id, data),
      deleteServiceDisplayDayPart: (id: string) => ch.deleteServiceDisplayDayPart(id),
      getHistoricalEvents: (deviceUrl: string, at: string, start: number, end: number) =>
        VmsApi(deviceUrl, false, at).getHistoricalEvents(start, end),
    };
  }, [accessToken]);

  return (
    <LocaleProvider locale={locale}>
      <SDMClientProvider client={sdmClient}>
        <SDMAnalyticsProvider logger={AnalyticsLogger}>
          <DeviceLocalityProviderWrapper provider={deviceLocalityProvider}>
            <PeopleTrackingProvider hook={usePeopleTrackingCamera}>
              <LiveVideoProvider player={SdmLiveFeedPlayer}>
                <ServiceDisplayMonitorRoutesInner />
              </LiveVideoProvider>
            </PeopleTrackingProvider>
          </DeviceLocalityProviderWrapper>
        </SDMAnalyticsProvider>
      </SDMClientProvider>
    </LocaleProvider>
  );
}

const VideoWallPageWrapper = Loadable({
  loader: () => import('components/VideoWallPage/VideoWallPageWrapper'),
});

const VerificationCenterRoutes = Loadable({
  loader: () => import('components/VerificationCenter/VerificationCenterRoutes'),
});

const VerifEye4VerificationPage = Loadable({
  loader: () => import('components/VerifEye4Page/VerifEye4VerificationPage'),
});

const DataExplorerVerifEyeDeck = Loadable({
  loader: () => import('@solinkcorp/native-data-explorer/verifEye/DataExplorerVerifEyeDeck'),
});

interface TopLevelRoutesProps {
  updateLiveToggle: (toggle: boolean) => void;
  onShowAllNotifications: () => void;
  inboxCount: number;
}

const TopLevelRoutes: React.FC<TopLevelRoutesProps> = (props) => {
  const user = useUserSession();
  const isGuest = user.type === 'guest';
  const canEnableAlarmVerification = useUserHasFeature('enableAlarmVerification');
  const hasAccessToAiServiceDisplay = useUserHasFeature('sdmReadAccess');

  const dispatch = useDispatch();
  const locale = useLocale();
  const playingCamerasController = usePlayingCamerasContext();

  const hasNoPhysicalLocations = useFluxStore(LocationStore, (store) =>
    store.getHasNoPhysicalLocations(),
  );

  const hasVerifEyeGroups = userHasVerifEyeGroups(user);

  const topLevelRoutes = [
    {
      path: '*',
      defaultPath: '/',
      hasPermission: hasVerifEyeGroups,
      element: <VerifEyeLandingPage />,
    },
    {
      path: '/ai-service-display/*',
      defaultPath: '/ai-service-display',
      hasPermission: hasAccessToAiServiceDisplay && !hasVerifEyeGroups,
      element: <ServiceDisplayMonitorRoutes />,
    },
    {
      path: `/${VERIFICATION_ROUTE}/*`,
      defaultPath: `/${VERIFICATION_ROUTE}`,
      hasPermission: canEnableAlarmVerification,
      element: <VerificationCenterRoutes updateLiveToggle={props.updateLiveToggle} />,
    },
    {
      path: '/verifeye4/verify/:eventId',
      defaultPath: '/verifeye4',
      hasPermission: canViewVerifeyePage(user) && !FeaturesUtil.isLiveViewOnly(),
      element: <VerifEye4VerificationPage />,
    },
    {
      // Standalone Data Explorer VerifEye review deck (SL-15900), full-screen —
      // rendered here (outside NavigationFrame) so it bypasses the app chrome,
      // mirroring the VerifEye4 deck. Gated solely on the access flag so a pure
      // reviewer can open it with no other DE entitlement.
      path: `${DATA_EXPLORER_VERIFEYE_URL}/verify/:eventId`,
      defaultPath: `${DATA_EXPLORER_VERIFEYE_URL}/open`,
      hasPermission: canViewDataExplorerVerifEye(user),
      element: <DataExplorerVerifEyeDeck />,
    },
    {
      path: '/wall/*',
      defaultPath: '/wall',
      hasPermission: !isGuest && !hasVerifEyeGroups,
      element: <VideoWallPageWrapper />,
    },
  ];

  const accessibleTopLevelRoutes = topLevelRoutes.filter((route) => route.hasPermission);

  const navigation = generateMenu(
    dispatch,
    locale,
    hasNoPhysicalLocations,
    playingCamerasController,
    user,
  );

  const accessibleNavigation = navigation.filter((nav) => nav.show);

  const dashboardRoute = accessibleNavigation.find((nav) => nav.route === '/dashboard');
  const defaultPath =
    dashboardRoute?.route ||
    accessibleNavigation[0]?.route ||
    accessibleTopLevelRoutes[0]?.defaultPath ||
    '/not-found';

  return (
    <Routes>
      <Route path="/" element={<VerifyAccess defaultPath={defaultPath} />} />
      {topLevelRoutes
        .filter((route) => route.hasPermission)
        .map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
      <Route
        path="*"
        element={
          <NavigationFrame {...props} navigation={accessibleNavigation} defaultPath={defaultPath} />
        }
      />
    </Routes>
  );
};

export default React.memo(TopLevelRoutes);
